import NavButton from '@atoms/NavButton/NavButton';
import HorizontalDirectionEnum from '@models/enums/HorizontalDirectionEnum';
import Carousel from '@molecules/Carousel/Carousel';
import duplicateArrayItems from '@utils/helpers/duplicateArrayItems';
import useBreakpoint from '@utils/hooks/useBreakpoint';
import { ReactNode, useEffect, useState } from 'react';
import SwiperType from 'swiper';
import { Autoplay } from 'swiper/modules';
import { Swiper } from 'swiper/types';
import { AutoplayOptions } from 'swiper/types/modules/autoplay';

interface Props<T> {
  readonly id: string;
  readonly items: T[];
  readonly autoplay?: AutoplayOptions;
  readonly children: (itemsToDisplay: T[]) => ReactNode;
  readonly loop?: boolean;
}

const StripCarousel = <T,>({
  children,
  id,
  items,
  autoplay,
  loop = false,
}: Props<T>) => {
  const [shouldLoop, setShouldLoop] = useState(false);
  const [swiperInstance, setSwiperInstance] = useState<Swiper>();
  const breakPoint = useBreakpoint();
  const [hasMoreItemsToShow, setHasMoreItemsToShow] = useState(false);

  const itemsToDisplay = shouldLoop
    ? duplicateArrayItems(items, items.length + 1)
    : items;

  const handleSwiperUpdate = (swiper: SwiperType) => {
    const swiperSlidesPerView = swiper?.params?.slidesPerView;
    const currentSlidesPerView =
      swiperSlidesPerView && typeof swiperSlidesPerView === 'number'
        ? swiperSlidesPerView
        : 0;

    setHasMoreItemsToShow(items.length > currentSlidesPerView);
  };

  useEffect(() => {
    const shouldLoopCondition =
      ((!!breakPoint && breakPoint !== 'sm') || loop) && hasMoreItemsToShow;

    setShouldLoop(shouldLoopCondition);
  }, [breakPoint, hasMoreItemsToShow, loop]);

  useEffect(() => {
    swiperInstance?.navigation.update();

    if (shouldLoop && autoplay) swiperInstance?.autoplay.start();
  }, [autoplay, shouldLoop, swiperInstance]);

  return (
    <div className="overflow-hidden">
      <div className="overflow-swiper container lg:grid lg:grid-cols-12">
        <div className="3xl:px-32 col-span-full flex flex-row content-center items-center gap-13 px-0 xl:col-span-10 xl:col-start-2 xl:gap-[100px]">
          <div className="hidden lg:block">
            <NavButton
              direction={HorizontalDirectionEnum.Left}
              id={id}
              disabled={!shouldLoop}
            />
          </div>

          <Carousel
            spaceBetween={16}
            loop={shouldLoop}
            navId={id}
            className="overflow-large w-full"
            onResize={handleSwiperUpdate}
            onInit={handleSwiperUpdate}
            onSwiper={setSwiperInstance}
            additionalModules={autoplay ? [Autoplay] : undefined}
            autoplay={autoplay}
            breakpoints={{
              0: {
                // Swiper v10 needs a value greater than 1 to properly show the prev/next slides when looping
                slidesPerView: 1.001,
                centeredSlides: true,
              },
              1024: {
                slidesPerView: 1,
                centeredSlides: false,
              },
              1440: {
                slidesPerView: 2,
                centeredSlides: false,
              },
            }}
          >
            {children(itemsToDisplay)}
          </Carousel>

          <div className="hidden lg:block">
            <NavButton
              direction={HorizontalDirectionEnum.Right}
              id={id}
              disabled={!shouldLoop}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripCarousel;
